import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import type { FeatureType, PackedLayer } from '../slice/types';
import { FEATURE_COLORS, FEATURE_TYPES } from '../slice/types';
import type { PreviewState } from '../preview/previewStore';

function emitSegs(pts: number[], z: number, out: number[]) {
  for (let k = 0; k + 3 < pts.length; k += 2) {
    const dx = pts[k + 2] - pts[k];
    const dy = pts[k + 3] - pts[k + 1];
    if (dx * dx + dy * dy < 0.004) continue;
    out.push(pts[k], z, pts[k + 1], pts[k + 2], z, pts[k + 3]);
  }
}

function packedToSegments(xy: Float32Array, z: number, out: number[], fraction = 1) {
  const pts: number[] = [];
  for (let i = 0; i < xy.length; i += 2) {
    if (!Number.isFinite(xy[i])) {
      if (pts.length >= 4) emitSegs(pts, z, out);
      pts.length = 0;
      continue;
    }
    pts.push(xy[i], xy[i + 1]);
  }
  if (pts.length >= 4) emitSegs(pts, z, out);
  if (fraction < 1 && out.length) {
    const keep = Math.max(6, Math.floor(out.length * fraction / 6) * 6);
    out.length = keep;
  }
}

function makeMat(color: number, width: number, opacity: number) {
  return new LineMaterial({
    color,
    linewidth: width,
    worldUnits: false,
    transparent: opacity < 0.99,
    opacity,
    depthTest: true,
    toneMapped: false,
    alphaToCoverage: false,
  });
}

export class ToolpathRenderer {
  group = new THREE.Group();
  private past: Partial<Record<FeatureType, { line: LineSegments2; geom: LineSegmentsGeometry; mat: LineMaterial }>> = {};
  private current: Partial<Record<FeatureType, { line: LineSegments2; geom: LineSegmentsGeometry; mat: LineMaterial }>> = {};
  private lastKey = '';
  private resolution = new THREE.Vector2(1024, 625);
  private unpacked: Array<Partial<Record<FeatureType, number[]>>> = [];
  private unpackedLen = 0;

  constructor() {
    this.group.name = 'toolpaths';
    for (const f of FEATURE_TYPES) {
      const pg = new LineSegmentsGeometry();
      const pm = makeMat(FEATURE_COLORS[f], 1.7, 0.55);
      const pl = new LineSegments2(pg, pm);
      pl.visible = false;
      pl.renderOrder = 2;
      this.group.add(pl);
      this.past[f] = { line: pl, geom: pg, mat: pm };
      const cg = new LineSegmentsGeometry();
      const cm = makeMat(FEATURE_COLORS[f], 2.6, 1);
      const cl = new LineSegments2(cg, cm);
      cl.renderOrder = 3;
      cl.visible = false;
      this.group.add(cl);
      this.current[f] = { line: cl, geom: cg, mat: cm };
    }
  }

  setSize(w: number, h: number) {
    this.resolution.set(w, h);
    for (const f of FEATURE_TYPES) {
      this.past[f]?.mat.resolution.copy(this.resolution);
      this.current[f]?.mat.resolution.copy(this.resolution);
    }
  }

  dispose() {
    for (const f of FEATURE_TYPES) {
      this.past[f]?.geom.dispose();
      this.past[f]?.mat.dispose();
      this.current[f]?.geom.dispose();
      this.current[f]?.mat.dispose();
    }
  }

  update(preview: PreviewState, filamentHex: number, lineWidth: number) {
    const layers = preview.layers;
    const idx = Math.min(preview.currentLayer, Math.max(0, layers.length - 1));
    const key = `${layers.length}:${idx}:${preview.layerDraw}:${preview.colorBy}:${filamentHex}:${JSON.stringify(preview.hidden)}:${lineWidth}:${preview.status}`;
    if (key === this.lastKey) return;
    const dimW = 1.8;
    const curW = 2.8;
    void lineWidth;

    if (layers.length < this.unpackedLen) {
      this.unpacked = [];
      this.unpackedLen = 0;
    }
    for (let i = this.unpackedLen; i < layers.length; i++) {
      const segs: Partial<Record<FeatureType, number[]>> = {};
      for (const f of FEATURE_TYPES) {
        const packed = layers[i]?.paths[f];
        const arr: number[] = [];
        if (packed && packed.length) packedToSegments(packed, layers[i].z, arr, 1);
        segs[f] = arr;
      }
      this.unpacked[i] = segs;
    }
    this.unpackedLen = layers.length;

    for (const f of FEATURE_TYPES) {
      const hidden = !!preview.hidden[f];
      const color = preview.colorBy === 'filament' ? filamentHex : FEATURE_COLORS[f];
      const p = this.past[f]!;
      const c = this.current[f]!;
      p.mat.color.setHex(color);
      c.mat.color.setHex(color);
      p.mat.opacity = preview.colorBy === 'filament' ? 0.42 : 0.52;
      c.mat.opacity = 1;
      p.mat.linewidth = dimW;
      c.mat.linewidth = curW;
      p.mat.needsUpdate = true;
      c.mat.needsUpdate = true;

      if (hidden || !layers.length) {
        p.line.visible = false;
        c.line.visible = false;
        continue;
      }

      const pastPts: number[] = [];
      const showPast = preview.status !== 'slicing';
      if (showPast) {
        for (let i = 0; i < idx; i++) {
          const src = this.unpacked[i]?.[f];
          if (src && src.length) pastPts.push(...src);
        }
      }
      if (pastPts.length >= 6) {
        p.geom.setPositions(pastPts);
        p.line.visible = true;
      } else {
        p.line.visible = false;
      }

      const curPts: number[] = [];
      const src = this.unpacked[idx]?.[f];
      if (src && src.length) {
        if (preview.layerDraw >= 0.999) curPts.push(...src);
        else {
          const packed = layers[idx]?.paths[f];
          if (packed && packed.length) packedToSegments(packed, layers[idx].z, curPts, preview.layerDraw);
        }
      }
      if (curPts.length >= 6) {
        c.geom.setPositions(curPts);
        c.line.visible = true;
      } else {
        c.line.visible = false;
      }
    }
    this.lastKey = key;
  }

  lengthsUpTo(layers: PackedLayer[], index: number): number {
    let sum = 0;
    for (let i = 0; i <= index && i < layers.length; i++) {
      const L = layers[i].lengths;
      if (!L) continue;
      for (const k of FEATURE_TYPES) sum += L[k] || 0;
    }
    return sum;
  }
}
